import { useMemo, useState } from "react";
import type { RunTrace, SubCallTrace } from "../../lib/traceApi";
import { subCallAnchorId } from "../../lib/traceAnchors";

// RunOutliers — admin-only panel (plan §7.8.3) that surfaces the 5 most
// expensive and 5 longest sub-calls in a finished/in-flight run. Computed
// client-side from the loaded RunTrace so it shows up the instant the
// trace doc lands; no extra round-trip.
//
// Each row is a button that scrolls to the matching SubCallRow via the
// anchor IDs minted by feature 4 (run-trace permalink). We don't
// auto-expand the panel here because (a) the user may want to compare
// multiple outliers without expanding each, and (b) the deep-link/hash
// path (feature 4) already exercises the expansion code for the explicit
// "open this one" flow.

interface Outlier {
  subCall: SubCallTrace;
  anchorId: string;
  // Display fields denormalized once so render doesn't recompute.
  stage: string;
  layer: string;
}

type Mode = "cost" | "duration";

const TOP_N = 5;

interface RunOutliersProps {
  trace: RunTrace;
}

export function RunOutliers({ trace }: RunOutliersProps) {
  const allOutliers = useMemo(() => collectSubCalls(trace), [trace]);
  const [mode, setMode] = useState<Mode>("cost");
  const [filter, setFilter] = useState("");

  const filtered = useMemo(() => {
    if (filter.trim() === "") return allOutliers;
    const f = filter.toLowerCase();
    return allOutliers.filter(
      (o) =>
        o.subCall.sub_prompt_id.toLowerCase().includes(f) ||
        o.subCall.item_key.toLowerCase().includes(f) ||
        o.stage.toLowerCase().includes(f) ||
        o.layer.toLowerCase().includes(f),
    );
  }, [allOutliers, filter]);

  const byCost = useMemo(
    () =>
      [...filtered]
        .sort((a, b) => (b.subCall.total_cost_usd ?? 0) - (a.subCall.total_cost_usd ?? 0))
        .slice(0, TOP_N),
    [filtered],
  );
  const byDuration = useMemo(
    () =>
      [...filtered]
        .sort((a, b) => (b.subCall.duration_ms ?? 0) - (a.subCall.duration_ms ?? 0))
        .slice(0, TOP_N),
    [filtered],
  );

  if (allOutliers.length === 0) return null;

  const rows = mode === "cost" ? byCost : byDuration;
  const max =
    mode === "cost"
      ? Math.max(...rows.map((r) => r.subCall.total_cost_usd ?? 0), 0.0001)
      : Math.max(...rows.map((r) => r.subCall.duration_ms ?? 0), 1);

  return (
    <details className="border border-gray-700 rounded bg-gray-900 mb-3">
      <summary className="cursor-pointer text-sm px-3 py-2 hover:bg-gray-800 text-ink-200 font-mono">
        Outliers
        <span className="ml-2 text-gray-500 text-xs">
          (top {TOP_N} by cost / duration — click to expand)
        </span>
      </summary>
      <div className="p-3 border-t border-gray-700">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex gap-1">
            <button
              onClick={() => setMode("cost")}
              className={`px-2 py-1 text-xs border rounded ${mode === "cost" ? "bg-gray-700 border-blue-400" : "border-gray-600"}`}
            >
              Top {TOP_N} by cost
            </button>
            <button
              onClick={() => setMode("duration")}
              className={`px-2 py-1 text-xs border rounded ${mode === "duration" ? "bg-gray-700 border-blue-400" : "border-gray-600"}`}
            >
              Top {TOP_N} by duration
            </button>
          </div>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter by id/item/stage/layer…"
            className="ml-auto bg-gray-950 border border-gray-700 text-xs px-2 py-1 rounded text-ink-100 placeholder-gray-500 w-64"
          />
        </div>
        <ol className="space-y-1">
          {rows.map((row, i) => (
            <OutlierBar key={row.anchorId + ":" + i} row={row} mode={mode} max={max} rank={i + 1} />
          ))}
        </ol>
      </div>
    </details>
  );
}

function OutlierBar({
  row,
  mode,
  max,
  rank,
}: {
  row: Outlier;
  mode: Mode;
  max: number;
  rank: number;
}) {
  const value =
    mode === "cost" ? row.subCall.total_cost_usd ?? 0 : row.subCall.duration_ms ?? 0;
  const widthPct = max > 0 ? (value / max) * 100 : 0;
  const label =
    mode === "cost"
      ? `$${value.toFixed(4)}`
      : `${(value / 1000).toFixed(2)}s`;
  const barColor = mode === "cost" ? "bg-amber-700/60" : "bg-sky-700/60";

  return (
    <li className="text-xs">
      <a
        href={`#${row.anchorId}`}
        onClick={(e) => {
          e.preventDefault();
          scrollToAnchor(row.anchorId);
        }}
        className="block hover:bg-gray-800 rounded px-1 py-0.5"
      >
        <div className="flex items-center gap-2">
          <span className="w-5 text-gray-500 text-right">{rank}.</span>
          <span className="font-mono text-ink-100 truncate">
            {row.subCall.sub_prompt_id}
            {row.subCall.item_key !== "" && (
              <span className="text-gray-500">[{row.subCall.item_key}]</span>
            )}
          </span>
          <span className="text-gray-500 ml-1 truncate">
            ({row.stage}/{row.layer})
          </span>
          <span className="ml-auto font-mono text-ink-200">{label}</span>
        </div>
        <div className="ml-7 mt-0.5 h-1.5 bg-gray-800 rounded overflow-hidden">
          <div className={`h-full ${barColor}`} style={{ width: `${widthPct}%` }} />
        </div>
      </a>
    </li>
  );
}

function collectSubCalls(trace: RunTrace): Outlier[] {
  const out: Outlier[] = [];
  for (const stage of trace.stages ?? []) {
    for (const layer of stage.layers ?? []) {
      for (const sc of layer.sub_calls ?? []) {
        out.push({
          subCall: sc,
          anchorId: subCallAnchorId(sc),
          stage: stage.stage,
          layer: layer.name,
        });
      }
    }
  }
  return out;
}

// scrollToAnchor: replicate the hash-link behavior without polluting
// browser history (which would otherwise stack one entry per outlier
// click). RunTrace.tsx's mount-time hash handler is responsible for the
// initial deep-link case; this handler covers the in-page navigation case.
function scrollToAnchor(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Update hash silently so the URL stays shareable.
  history.replaceState(null, "", `#${anchorId}`);
  // Trigger the expansion via custom event; SubCallRow listens.
  window.dispatchEvent(new CustomEvent("trace-anchor-focus", { detail: anchorId }));
}
